import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import GlassCard from "./GlassCard";
import GoldButton from "./GoldButton";
import GhostButton from "./GhostButton";
import { formatPrice } from "../lib/billing";
import { cn } from "../lib/utils";

export function PlanCard({ plan, isCurrent, onChoose, busy, className }) {
  const { t } = useTranslation();
  const isPro = plan.id === "pro";
  const priceText =
    plan.price_cents === 0 ? "$0" : formatPrice(plan.price_cents);

  return (
    <GlassCard
      className={cn(
        "p-6 sm:p-7 relative overflow-hidden",
        isPro && "border-[#D4AF37]/45",
        className,
      )}
      data-testid={`plan-card-${plan.id}`}
    >
      {isPro && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(420px 200px at 100% 0%, rgba(212,175,55,0.22), transparent)",
          }}
        />
      )}

      <div className="relative">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-white/50">
              {t("dashboard.planLabel")}
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-white mt-1 tracking-tight">
              {t(`plan.${plan.id}`)}
            </h3>
          </div>
          {isCurrent && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/12 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#F4D77A]"
              data-testid={`plan-card-${plan.id}-current`}
            >
              {t("billing.currentPlan")}
            </span>
          )}
        </div>

        <div className="mt-4 flex items-baseline gap-1">
          <span className="font-display text-4xl text-white">{priceText}</span>
          {plan.price_cents > 0 && (
            <span className="text-sm text-white/45">{t("plan.perMonth")}</span>
          )}
        </div>
        {plan.nxt_grant ? (
          <div className="mt-1 text-xs text-[#D4AF37]">
            {t("plan.grant", { n: plan.nxt_grant })}
          </div>
        ) : null}

        <ul className="mt-5 space-y-2 text-sm text-white/80">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check
                className={cn(
                  "mt-0.5 h-4 w-4 shrink-0",
                  isPro ? "text-[#D4AF37]" : "text-white/55",
                )}
                aria-hidden
              />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6">
          {isCurrent ? (
            plan.id === "pro" ? (
              <GhostButton
                onClick={onChoose}
                disabled={busy}
                className="w-full"
                data-testid="plan-downgrade-btn"
              >
                {t("checkout.titleDowngrade")}
              </GhostButton>
            ) : (
              <GhostButton disabled className="w-full">
                {t("billing.currentPlan")}
              </GhostButton>
            )
          ) : isPro ? (
            <GoldButton
              onClick={onChoose}
              disabled={busy}
              className="w-full justify-center"
              data-testid="plan-upgrade-btn"
            >
              {t("dashboard.planCtaFree")}
            </GoldButton>
          ) : (
            <GhostButton
              onClick={onChoose}
              disabled={busy}
              className="w-full"
              data-testid="plan-free-btn"
            >
              {t("plan.free")}
            </GhostButton>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

export default PlanCard;
